import type {
  StoryboardImageResponse,
  StoryboardVideoResponse,
} from '@/services/api/storyboard-resources';
import type { StoryboardItem } from '@/types/preview';

/** 适配层：统一 ProjectElementTagResponse 和 StoryboardPlanResponse 的可读字段 */
export interface StoryboardPlanLike {
  tagLabel?: string;
  description?: string | null;
  resourceCount?: number;
  resourceUrl?: string | null;
}

interface MapStoryboardImageToPreviewItemParams {
  image: StoryboardImageResponse;
  orderIndex?: number;
  tagById: ReadonlyMap<string, StoryboardPlanLike>;
}

interface MapStoryboardVideoToPreviewItemParams {
  video: StoryboardVideoResponse;
  fallbackThumbnailUrl?: string;
}

interface SortableStoryboardVideoPreviewItem {
  isActive?: boolean;
  displayOrder?: number | null;
  createdAt?: string | null;
  version?: number | null;
}

interface ActivatableStoryboardVideoPreviewItem extends SortableStoryboardVideoPreviewItem {
  videoId: string;
  parentId?: string | null;
  isActive: boolean;
}

interface GroupableStoryboardVideoPreviewItem extends SortableStoryboardVideoPreviewItem {
  id: string;
  type?: string;
  isFinalized?: boolean;
}

const UUID_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function getPreviewVideoLineageRootId(
  video: Pick<ActivatableStoryboardVideoPreviewItem, 'videoId' | 'parentId'>
): string {
  return video.parentId ?? video.videoId;
}

function buildShotNumber(orderIndex?: number): string | undefined {
  if (typeof orderIndex !== 'number') return undefined;
  return `S${String(orderIndex).padStart(2, '0')}`;
}

function resolveCreatedAtTime(value?: string | null): number {
  if (!value) return 0;
  const time = Date.parse(value);
  return Number.isNaN(time) ? 0 : time;
}

function compareStoryboardVideoPreviewOrder<T extends SortableStoryboardVideoPreviewItem>(
  a: T,
  b: T
): number {
  const aActive = a.isActive === true;
  const bActive = b.isActive === true;

  if (aActive !== bActive) {
    return aActive ? -1 : 1;
  }

  const aDisplayOrder = a.displayOrder;
  const bDisplayOrder = b.displayOrder;
  if (typeof aDisplayOrder === 'number' && typeof bDisplayOrder === 'number') {
    const displayOrderDiff = aDisplayOrder - bDisplayOrder;
    if (displayOrderDiff !== 0) return displayOrderDiff;
  } else if (typeof aDisplayOrder === 'number' || typeof bDisplayOrder === 'number') {
    return typeof aDisplayOrder === 'number' ? -1 : 1;
  }

  const createdAtDiff = resolveCreatedAtTime(b.createdAt) - resolveCreatedAtTime(a.createdAt);
  if (createdAtDiff !== 0) return createdAtDiff;

  return (b.version ?? 0) - (a.version ?? 0);
}

export function sortStoryboardVideoPreviewItems<T extends SortableStoryboardVideoPreviewItem>(
  items: readonly T[]
): T[] {
  return [...items].sort(compareStoryboardVideoPreviewOrder);
}

export function markStoryboardVideoActiveInList<T extends ActivatableStoryboardVideoPreviewItem>(
  items: readonly T[],
  activeVideoId: string
): T[] {
  const activeVideo = items.find((video) => video.videoId === activeVideoId);
  const activeRootId = activeVideo ? getPreviewVideoLineageRootId(activeVideo) : null;
  return sortStoryboardVideoPreviewItems(
    items.map((video) => ({
      ...video,
      isActive:
        video.videoId === activeVideoId ||
        (activeRootId !== null &&
          getPreviewVideoLineageRootId(video) !== activeRootId &&
          video.isActive),
    })) as T[]
  );
}

export function setStoryboardVideoActiveInList<T extends ActivatableStoryboardVideoPreviewItem>(
  items: readonly T[],
  videoId: string,
  isActive: boolean
): T[] {
  const targetVideo = items.find((video) => video.videoId === videoId);
  const targetRootId = targetVideo ? getPreviewVideoLineageRootId(targetVideo) : null;
  return sortStoryboardVideoPreviewItems(
    items.map((video) => {
      if (video.videoId === videoId) {
        return {
          ...video,
          isActive,
          isFinalized: true,
        };
      }
      if (
        isActive &&
        targetRootId !== null &&
        getPreviewVideoLineageRootId(video) === targetRootId
      ) {
        return {
          ...video,
          isActive: false,
        };
      }
      return video;
    }) as T[]
  );
}

export function isStoryboardVideoBackupStatus(
  item: Pick<SortableStoryboardVideoPreviewItem, 'isActive'>
): boolean {
  return item.isActive !== true;
}

export function isPersistedStoryboardVideoPreviewItem(
  item: Pick<GroupableStoryboardVideoPreviewItem, 'id' | 'type'>
): boolean {
  return item.type === 'video' && UUID_ID_RE.test(item.id);
}

export function groupStoryboardVideoPreviewItems<T extends GroupableStoryboardVideoPreviewItem>(
  items: readonly T[]
): {
  mainVideoItems: T[];
  backupVideoItems: T[];
  passiveVideoItems: T[];
} {
  const mainVideoItems: T[] = [];
  const backupVideoItems: T[] = [];
  const passiveVideoItems: T[] = [];

  for (const item of items) {
    if (!isPersistedStoryboardVideoPreviewItem(item)) {
      passiveVideoItems.push(item);
      continue;
    }

    if (item.isActive === true) {
      mainVideoItems.push(item);
    } else {
      backupVideoItems.push(item);
    }
  }

  return {
    mainVideoItems: sortStoryboardVideoPreviewItems(mainVideoItems),
    backupVideoItems: sortStoryboardVideoPreviewItems(backupVideoItems),
    passiveVideoItems,
  };
}

export function buildStoryboardVideoReorderPayload<T extends GroupableStoryboardVideoPreviewItem>(
  previousItems: readonly T[],
  reorderedItems: readonly T[],
  movedVideoId?: string
): {
  videoIds: string[];
  activeVideoIds: string[];
} {
  const videoIds = reorderedItems.map((item) => item.id);
  const activeVideoIds = new Set(
    reorderedItems.filter((item) => item.isActive === true).map((item) => item.id)
  );
  const movedItem = movedVideoId
    ? previousItems.find((item) => item.id === movedVideoId)
    : undefined;

  if (movedItem && movedVideoId) {
    const previousMainCount = previousItems.filter((item) => item.isActive === true).length;
    const nextIndex = reorderedItems.findIndex((item) => item.id === movedVideoId);

    if (nextIndex >= 0) {
      if (movedItem.isActive === true && nextIndex >= previousMainCount) {
        activeVideoIds.delete(movedVideoId);
      }
      if (movedItem.isActive !== true && nextIndex <= previousMainCount) {
        activeVideoIds.add(movedVideoId);
      }
    }
  }

  return {
    videoIds,
    activeVideoIds: videoIds.filter((videoId) => activeVideoIds.has(videoId)),
  };
}

function normalizeStoryboardVideoStatus(
  statusCode: number,
  statusStr?: string | null
): NonNullable<StoryboardItem['status']> {
  const normalized = (statusStr || '').trim().toLowerCase();
  if (
    normalized === 'pending' ||
    normalized === 'processing' ||
    normalized === 'completed' ||
    normalized === 'failed' ||
    normalized === 'cancelled'
  ) {
    return normalized;
  }

  switch (statusCode) {
    case 0:
      return 'pending';
    case 1:
      return 'processing';
    case 2:
      return 'completed';
    case 3:
      return 'failed';
    default:
      return 'pending';
  }
}

export function formatPreviewByteSize(fileSize?: number | null): string {
  if (typeof fileSize !== 'number' || Number.isNaN(fileSize)) {
    return '未填写';
  }

  if (fileSize >= 1024 * 1024) {
    return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (fileSize >= 1024) {
    return `${(fileSize / 1024).toFixed(1)} KB`;
  }

  return `${fileSize} B`;
}

export function formatPreviewVideoDuration(duration?: string | number | null): string {
  if (duration === null || duration === undefined || duration === '') {
    return '未填写';
  }

  const normalized =
    typeof duration === 'number' ? duration : Number.parseFloat(String(duration).trim());

  if (Number.isNaN(normalized)) {
    return '未填写';
  }

  if (Number.isInteger(normalized)) {
    return `${normalized} 秒`;
  }

  return `${normalized.toFixed(1).replace(/\.0$/, '')} 秒`;
}

export function mapStoryboardImageToPreviewItem({
  image,
  orderIndex,
  tagById,
}: MapStoryboardImageToPreviewItemParams): StoryboardItem {
  const effectivePlanId = image.planId ?? image.tagId;
  const relatedTag = effectivePlanId ? tagById.get(effectivePlanId) : undefined;

  return {
    id: image.imageId,
    title: image.name ?? '',
    description: image.description ?? '',
    thumbnailUrl: image.url,
    type: 'storyboard',
    shotNumber: buildShotNumber(orderIndex),
    isActive: image.isActive,
    name: image.name,
    tagId: effectivePlanId ?? undefined,
    tagLabel: relatedTag?.tagLabel,
    tagDescription: relatedTag?.description ?? undefined,
    tagResourceCount: relatedTag?.resourceCount,
    tagResourceUrl: relatedTag?.resourceUrl ?? undefined,
    aiModel: image.aiModel,
    aiPrompt: image.aiPrompt,
    aiPromptId: image.aiPromptId,
    aiParams: image.aiParams,
    status: image.statusStr as StoryboardItem['status'],
    statusStr: image.statusStr,
    version: image.version,
    width: image.width,
    height: image.height,
    fileSize: image.fileSize,
    format: image.format,
    humanAssetType: image.humanAssetType ?? null,
    humanAssetStatus: image.humanAssetStatus ?? null,
    errorMessage: image.errorMessage,
    checksum: image.checksum,
    storagePath: image.storagePath,
    storageProvider: image.storageProvider,
    createdAt: image.createdAt,
    completedAt: image.completedAt,
  };
}

export function mapStoryboardVideoToPreviewItem({
  video,
  fallbackThumbnailUrl,
}: MapStoryboardVideoToPreviewItemParams): StoryboardItem {
  const status = normalizeStoryboardVideoStatus(video.status, video.statusStr);
  return {
    id: video.videoId,
    title: video.name || `视频方案 ${video.version}`,
    description: video.description ?? '',
    thumbnailUrl: video.coverUrl || fallbackThumbnailUrl || '',
    videoUrl: status === 'completed' ? video.url : undefined,
    type: 'video',
    tagId: video.planId ?? undefined,
    status,
    statusStr: video.statusStr,
    progress: video.progress ?? undefined,
    isActive: video.isActive,
    isFinalized: video.isFinalized ?? video.isActive,
    parentId: video.parentId ?? null,
    sourceId: video.sourceId ?? null,
    sourceVideoId: video.sourceVideoId ?? null,
    sourceRootVideoId: video.sourceRootVideoId ?? null,
    sourceOriginId: video.sourceOriginId ?? null,
    editAction: video.editAction ?? null,
    displayOrder: video.displayOrder,
    aiModel: video.aiModel,
    aiPrompt: video.aiPrompt ?? undefined,
    aiParams: video.aiParams,
    humanAssetType: video.humanAssetType ?? null,
    humanAssetStatus: video.humanAssetStatus ?? null,
    version: video.version,
    duration: video.duration == null ? undefined : String(video.duration),
    resolution: video.resolution ?? undefined,
    frameRate: video.frameRate ?? undefined,
    fileSize: video.fileSize ?? undefined,
    format: video.format ?? undefined,
    errorMessage: video.errorMessage ?? undefined,
    checksum: video.checksum,
    storagePath: video.storagePath,
    storageProvider: video.storageProvider ?? undefined,
    createdAt: video.createdAt ?? undefined,
    completedAt: video.completedAt ?? undefined,
  };
}

export function resolveStoryboardInfoPanelItem(
  currentItem?: StoryboardItem,
  schemeItem?: StoryboardItem
): StoryboardItem | undefined {
  return currentItem ?? schemeItem;
}
